export const tokens = {
    colors: {
        // Navy — kolor przewodni
        navy50:  '#E8EDF5',
        navy100: '#C5D0E3',
        navy200: '#A2B3D1',
        navy300: '#7F96BF',
        navy400: '#3D506E',
        navy500: '#212B36',
        navy600: '#1E293B',
        navy700: '#1D2A41',
        navy800: '#152033',
        navy900: '#0F1825',

        // Slate — tekst i UI
        slate50:  '#F5F5F5',
        slate100: '#F8FAFC',
        slate200: '#CBD5E1',
        slate300: '#94A3B8',
        slate400: '#64748B',
        slate500: '#495466',
        slate600: '#475569',
        slate700: '#334155',
        slate800: '#1E2A3A',
        slate900: '#0F172A',

        // Backgrounds
        gray50:  '#FAFAFA',
        gray100: '#F3F4F6',
        gray200: '#EEEEEE',
        gray300: '#E0E0E0',
        gray400: '#C7C7C7',
        gray500: '#A7B2BC',
        gray600: '#979DA1',
        gray700: '#6B7280',
        gray800: '#4A5361',
        gray900: '#232B36',

        black:   '#000000',
        white:   '#FFFFFF',

        // red — error
        red50:  '#FEF2F2',
        red100: '#FEE2E2',
        red400: '#F87171',
        red500: '#EF4444',
        red700: '#B91C1C',
        red800: '#991B1B',
        red900: '#7F1D1D',

        // Blue — akcenty, badge, dropzone, linki
        blue50:  '#EFF6FF',
        blue100: '#DBEAFE',
        blue200: '#BFDBFE',
        blue300: '#93C5FD',
        blue400: '#60A5FA',
        blue500: '#3B82F6',
        blue600: '#2563EB',
        blue700: '#1D4ED8',
        blue800: '#1E3A8A',
        blue900: '#1E3052',

        // Green — success
        green50:  '#F0FDF4',
        green100: '#DCFCE7',
        green300: '#86EFAC',
        green500: '#22C55E',
        green600: '#16A34A',
        green700: '#15803D',
        green800: '#166534',
        green900: '#14532D',

        // Amber — warning
        amber50:  '#FFFBEB',
        amber100: '#FEF3C7',
        amber300: '#FCD34D',
        amber500: '#F59E0B',
        amber600: '#D97706',
        amber700: '#B45309',
        amber800: '#92400E',
        amber900: '#78350F',
    },

    fontFamily: {
        sans: "'Roboto', system-ui, -apple-system, 'Segoe UI', 'Helvetica Neue', Arial, sans-serif",
        mono: "'Roboto Mono', ui-monospace, 'Cascadia Mono', Consolas, 'Courier New', monospace",
    },

    fontSize: {
        xxs:    '0.65rem',      // 10,6px
        xs:     '0.75rem',      // 12px
        sm:     '0.875rem',     // 14px
        base:   '1rem',         // 16px
        lg:     '1.25rem',      // 20px
        xl:     '1.5rem',       // 24px
        '2xl':  '1.875rem',     // 28px
        '3xl':  '1.875rem',
        '4xl':  '2.25rem',      // 36px
    },

    fontWeight: {
        normal:     400,
        medium:     500,
        semibold:   700,
        bold:       700,
    },

    lineHeight: {
        tight:  1.25,
        normal: 1.5,
        loose:  1.75,
    },

    letterSpacing: {
        base:   '0.025em',
        button: '0.05em',
        wide:   '0.1em',
    },

    spacing: {
        0:  '0',
        1:  '0.25rem',
        2:  '0.5rem',
        3:  '0.75rem',
        4:  '1rem',
        5:  '1.25rem',
        6:  '1.5rem',
        8:  '2rem',
        10: '2.5rem',
        12: '3rem',
        16: '4rem',
    },

    radius: {
        none:   '0',
        xs:     '4px',
        sm:     '8px',
        md:     '12px',
        lg:     '16px',
        xl:     '20px',
        '2xl':  '24px',
        '3xl':  '28px',
        '4xl':  '32px',
        full:   '9999px',
    },

    shadow: {
        sm: '0 1px 2px 0 rgba(0, 0, 0, 0.1)',
        md: '0 4px 6px -1px rgba(0, 0, 0, 0.2)',
        lg: '0 10px 15px -3px rgba(0, 0, 0, 0.2)',
        xl: '0 20px 25px -5px rgba(0, 0, 0, 0.2)',
    },

    zIndex: {
        base:     0,
        raised:   10,
        dropdown: 100,
        overlay:  200,
        modal:    300,
        toast:    400,
    },

    transition: {
        fast:   '150ms ease',
        normal: '250ms ease',
        slow:   '400ms ease',
    },
} as const;

export const fileFormatTokens = {
    light: {
        TXT:  { fg: '#4A5361', bg: '#F3F4F6' },
        PDF:  { fg: '#4527A0', bg: '#EDE7F6' },
        JPEG: { fg: '#1B5E20', bg: '#E8F5E9' },
        JPG:  { fg: '#1B5E20', bg: '#E8F5E9' },
        PNG:  { fg: '#E65100', bg: '#FFF3E0' },
        DOCX: { fg: '#0D47A1', bg: '#E3F2FD' },
        DOC:  { fg: '#0D47A1', bg: '#E3F2FD' },
        XLSX: { fg: '#2E7D32', bg: '#E8F5E9' },
        XLS:  { fg: '#2E7D32', bg: '#E8F5E9' },
        PPTX: { fg: '#880E4F', bg: '#FCE4EC' },
        PPT:  { fg: '#880E4F', bg: '#FCE4EC' },
        ODT:  { fg: '#006064', bg: '#E0F7FA' },
        RTF:  { fg: '#006064', bg: '#E0F7FA' },
    },
    dark: {
        TXT:  { fg: '#A8ACAF', bg: '#2A2D30' },
        PDF:  { fg: '#B39DDB', bg: '#1E1730' },
        JPEG: { fg: '#81C784', bg: '#132017' },
        JPG:  { fg: '#81C784', bg: '#132017' },
        PNG:  { fg: '#FFB74D', bg: '#2A1F0E' },
        DOCX: { fg: '#64B5F6', bg: '#0D1929' },
        DOC:  { fg: '#64B5F6', bg: '#0D1929' },
        XLSX: { fg: '#66BB6A', bg: '#0F2211' },
        XLS:  { fg: '#66BB6A', bg: '#0F2211' },
        PPTX: { fg: '#F48FB1', bg: '#2A0D1A' },
        PPT:  { fg: '#F48FB1', bg: '#2A0D1A' },
        RTF:  { fg: '#4DD0E1', bg: '#062528' },
    },
} as const;

export type FileFormat = keyof typeof fileFormatTokens.light;

export const semanticColors = {
    orange: {
        light: { fg: '#F97316', bg: '#FFF7ED' },
        dark:  { fg: '#EA580C', bg: '#FFEDD5' },
    },
    red: {
        light: { fg: '#F0544F', bg: '#FEF2F2' },
        dark:  { fg: '#DC2626', bg: '#FEE2E2' },
    },
    pink: {
        light: { fg: '#F472B6', bg: '#FDF2F8' },
        dark:  { fg: '#DB2777', bg: '#FCE7F3' },
    },
    purple: {
        light: { fg: '#A855F7', bg: '#FAF5FF' },
        dark:  { fg: '#7C3AED', bg: '#EDE9FE' },
    },
    blue: {
        light: { fg: '#3B82F6', bg: '#EFF6FF' },
        dark:  { fg: '#2563EB', bg: '#DBEAFE' },
    },
    green: {
        light: { fg: '#22C55E', bg: '#F0FDF4' },
        dark:  { fg: '#16A34A', bg: '#DCFCE7' },
    },
    gray: {
        light: { fg: '#94A3B8', bg: '#F1F5F9' },
        dark:  { fg: '#64748B', bg: '#E2E8F0' },
    },
    yellow: {
        light: { fg: '#EAB308', bg: '#FEFCE8' },
        dark:  { fg: '#CA8A04', bg: '#FEF9C3' },
    },
} as const;

export type SemanticColor = keyof typeof semanticColors;
export type SemanticColorShade = 'light' | 'dark';
export type SemanticColorValue = typeof semanticColors[SemanticColor][SemanticColorShade];